import { useEffect, useRef, useState } from 'react';
import { EyeIcon, EyeOffIcon, SearchIcon, GitBranchIcon } from 'lucide-react';

import { LabelFilterControl } from '@/features/labels';
import { useCanWork } from '@/features/workspaces/canWork';
import {
  FOCUS_RING,
  HEADER_PILL,
  HEADER_PILL_ACTIVE,
  HEADER_PILL_IDLE,
} from '@/shared/components/pillClasses';
import type { LabelView } from '@/lib/api/generated/model';

import { BOARD_STATE_FILTERS, type BoardStateFilter } from '../searchParams';
import { TASK_STATE_DOT, taskStateWords } from '../statusPresentation';
import { useOverflowing } from '@/shared/hooks/useOverflowing';
import type { TaskNarrowing } from '../boardFilter';

/** How long typing must settle before it becomes a navigation. */
const DEBOUNCE_MS = 300;

export interface TaskFilterRowProps {
  /** The workspace's vocabulary — every label, not only the ones in use. */
  registry: readonly LabelView[];
  narrowing: TaskNarrowing;
  onChange: (next: TaskNarrowing) => void;
  /**
   * Which controls this surface offers. A page states what it can narrow by;
   * everything about *how* those controls look and behave stays here.
   */
  controls?: {
    search?: boolean;
    labels?: boolean;
    state?: boolean;
    hidden?: boolean;
    /**
     * Whether this surface offers "Show subtasks" — excluding tasks a
     * `linked_workflow` step created (`task.is_subtask`). Unlike `hidden`,
     * this is a plain `TaskNarrowing` field (`excludeSubtasks`): the backend
     * always returns subtasks now, so there is nothing to refetch, only
     * something to filter back out client-side.
     */
    subtasks?: boolean;
    /**
     * The row's own "Clear all". On unless switched off: the Tasks page reports
     * and clears its filters in a chip row of its own (spec 113), and a second
     * "Clear all" there would clear only half of what the first one does.
     */
    clearAll?: boolean;
  };
  /**
   * Whether hidden tasks are currently included, and how to change that.
   *
   * Lives in this row because `078` FR-017 asks for hidden tasks to be reachable
   * "through a filter", and this is the row that holds filters — it sat beside the row
   * as a separate button, which cost the board a whole line and put the requirement's
   * own word in the wrong place.
   *
   * It is *not* part of `TaskNarrowing`. Everything else here narrows what is already on
   * screen; this changes what the surface fetches. The divider before it says so
   * visually, and keeping it out of the narrowing type says so in the types — which is
   * also why "Clear all" leaves it alone: someone looking at hidden tasks has not
   * applied a filter they would want cleared by accident.
   */
  includeHidden?: boolean;
  onIncludeHiddenChange?: (next: boolean) => void;
  /**
   * How many tasks are in each state, where the surface knows.
   *
   * Given, the state pills carry their counts and a state holding nothing is
   * dropped — the board's rule, and the reason its summary can double as its
   * filter. The count must be taken from the board *before* the state filter is
   * applied, or the pills stop reporting the way back out of the one that is on.
   */
  stateCounts?: Record<BoardStateFilter, number>;
  /**
   * Bind `/` to the search box. True only where searching is the page's primary
   * act; on a board the key would fight the canvas.
   */
  slashFocusesSearch?: boolean;
  /**
   * `'inline'`: the search box and label picker join the caller's own row
   * instead of forming a row of their own, and the search box keeps a fixed
   * width rather than filling the line. For a page that adds its own controls
   * beside these (the Tasks page, spec 112); the board and Overview keep `'row'`.
   */
  layout?: 'row' | 'inline';
  /**
   * What the empty search box says. The Tasks page searches more than a board
   * does (spec 112 FR-009) and says so (spec 113 FR-010); the board and
   * Overview keep the default.
   */
  placeholder?: string;
  /**
   * The label picker's visible word. "Filter" on a board header; inside a
   * panel that is already all filters, a noun says which one (spec 271).
   */
  labelsTrigger?: string;
}

/**
 * Text, labels and state — the one filter row, on every surface that has one.
 *
 * There were three. The board put its state pills and a label control in the
 * page header; Overview put a label control there and nothing else; the tasks
 * page rendered a third arrangement, with a search box the other two had no way
 * to offer, as the first element of the page *body* instead. Moving between two
 * of those pages moved the control a reader was looking for, and each surface's
 * pills were drawn from its own copy of the state vocabulary.
 *
 * A page now says which controls it has and hands over one narrowing. What is
 * left to differ between pages is what genuinely differs: whether the state
 * pills carry counts, and whether `/` belongs to the search box.
 *
 * The controls combine with **AND**. Each one added narrows — a filter that
 * could widen the result is the confusion this product rejected once already,
 * in the label semantics 067 fixed.
 */
export function TaskFilterRow({
  registry,
  narrowing,
  onChange,
  controls = { search: true, labels: true, state: true },
  includeHidden = false,
  onIncludeHiddenChange,
  stateCounts,
  slashFocusesSearch = false,
  layout = 'row',
  placeholder = 'Search titles and descriptions…',
  labelsTrigger,
}: TaskFilterRowProps) {
  const inline = layout === 'inline';
  /*
    The field is local and the narrowing is debounced out of it.

    This input drives a navigation, so committing per keystroke would push one
    history entry per letter and refilter every task in the workspace between
    them. Holding the text locally keeps typing responsive while the address
    catches up.
  */
  // A reader's pill names a gate as waiting on a member (spec 193).
  const stateWords = taskStateWords(useCanWork());
  const [query, setQuery] = useState(narrowing.q ?? '');
  const committed = useRef(narrowing.q ?? '');
  const inputRef = useRef<HTMLInputElement>(null);
  const filters = useRef<HTMLDivElement>(null);
  useOverflowing(filters);
  // For the `/` hint, which is only worth showing while it could be used.
  const [focused, setFocused] = useState(false);

  /*
    `/` focuses the search, the convention for a page whose primary act is
    searching — and the shell already trains the gesture with its own `⌘K`.

    Ignored while a field already has focus, or the shortcut would insert a
    slash into whatever someone was typing.
  */
  useEffect(() => {
    if (!slashFocusesSearch) return;
    function onKeyDown(event: KeyboardEvent) {
      if (event.key !== '/' || event.metaKey || event.ctrlKey || event.altKey) {
        return;
      }
      const active = document.activeElement;
      const typing =
        active instanceof HTMLInputElement ||
        active instanceof HTMLTextAreaElement ||
        (active instanceof HTMLElement && active.isContentEditable);
      if (typing) return;

      event.preventDefault();
      inputRef.current?.focus();
    }
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [slashFocusesSearch]);

  // A query arriving from the address — a shared link, or Back — has to fill
  // the box, or the page looks narrowed for no stated reason. Guarded so it
  // does not fight what is being typed.
  useEffect(() => {
    const incoming = narrowing.q ?? '';
    if (incoming !== committed.current) {
      committed.current = incoming;
      setQuery(incoming);
    }
  }, [narrowing.q]);

  useEffect(() => {
    if (query === committed.current) return;
    const timer = setTimeout(() => {
      committed.current = query;
      onChange({ ...narrowing, q: query.trim() ? query.trim() : undefined });
    }, DEBOUNCE_MS);
    return () => {
      clearTimeout(timer);
    };
  }, [query, narrowing, onChange]);

  // A state with nothing in it has nothing to offer — unless it is the one
  // currently filtering, in which case removing it would strand the reader on
  // an empty board with no way back. Only a surface that supplied counts can
  // make that judgement.
  const states = stateCounts
    ? BOARD_STATE_FILTERS.filter(
        (state) => stateCounts[state] > 0 || narrowing.state === state,
      )
    : BOARD_STATE_FILTERS;

  return (
    <div
      className={
        // `contents`: no box of its own, so the children are items of the
        // caller's row and align with whatever sits beside them there.
        // On a phone the search has its own full-width line and the pills
        // scroll on the line under it (spec 269). One scrolling line for both
        // (spec 122 FR-013) let the search fill the screen and pushed every
        // filter past its edge, with nothing to say they were there.
        inline ? 'contents' : 'flex flex-wrap items-center gap-2'
      }
    >
      {controls.search ? (
        /*
          Capped. At `flex-1` this ran to roughly six hundred pixels of empty
          input on a wide screen — far wider than any query anyone types, and
          wide enough that the state pills read as an afterthought at the far
          edge.

          Shaped like `ui/input` rather than approximately like it: same height,
          radius, border role and focus ring. It cannot *be* that primitive —
          the icon lives inside the border with it — but there is no reason for
          the box around them to be a different box.
        */
        <div
          className={`border-input focus-within:ring-ring flex h-8 items-center gap-2 rounded-lg border px-2.5 pointer-coarse:h-11 transition-colors focus-within:ring-3 ${
            inline
              ? 'w-72 shrink-0 max-md:w-auto max-md:min-w-0 max-md:flex-1 max-md:basis-[calc(100%-3.25rem)]'
              : 'w-full max-w-sm min-w-56 max-md:max-w-none max-md:min-w-0'
          }`}
        >
          <SearchIcon
            aria-hidden="true"
            className="text-muted-foreground size-3.5 shrink-0"
          />
          <input
            ref={inputRef}
            type="search"
            aria-label="Search tasks"
            aria-keyshortcuts={slashFocusesSearch ? '/' : undefined}
            placeholder={placeholder}
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
            }}
            onFocus={() => {
              setFocused(true);
            }}
            onBlur={() => {
              setFocused(false);
            }}
            className="placeholder:text-muted-foreground min-w-0 flex-1 bg-transparent text-sm outline-none pointer-coarse:-my-px pointer-coarse:self-stretch"
          />
          {/*
            The shortcut, said where it applies (spec 113 FR-011) — only while
            pressing it would do something: not once the box has focus or text,
            and not on a page where `/` belongs to something else. Hidden from
            assistive technology, which reads `aria-keyshortcuts` instead.
          */}
          {slashFocusesSearch && !focused && query === '' ? (
            <kbd
              aria-hidden="true"
              data-testid="search-slash-hint"
              className="border-border text-muted-foreground bg-muted rounded border px-1.5 font-mono text-xs leading-4 max-md:hidden"
            >
              /
            </kbd>
          ) : null}
        </div>
      ) : null}

      {/* No box of its own from `md` up; on a phone, the pills' scrolling
          line, faded at its edge while more is to the right. */}
      <div
        ref={filters}
        className={
          inline
            ? 'contents'
            : 'contents max-md:overflow-fade max-md:scrollbar-none max-md:flex max-md:w-full max-md:items-center max-md:gap-2 max-md:overflow-x-auto max-md:*:shrink-0'
        }
      >
        {controls.labels ? (
          <LabelFilterControl
            registry={registry}
            narrowing={narrowing.labels}
            onChange={(next) => {
              onChange({ ...narrowing, labels: next });
            }}
            trigger={labelsTrigger}
          />
        ) : null}

        {controls.state ? (
          <ul className="flex flex-wrap items-center gap-2">
            {states.map((state) => {
              const active = narrowing.state === state;
              const words = stateWords[state];
              return (
                <li key={state}>
                  <button
                    type="button"
                    aria-pressed={active}
                    onClick={() => {
                      // Pressing the active filter again is how someone gets back
                      // out of it, so the same control both sets and clears.
                      onChange({
                        ...narrowing,
                        state: active ? undefined : state,
                      });
                    }}
                    className={`${HEADER_PILL} ${
                      active ? HEADER_PILL_ACTIVE : HEADER_PILL_IDLE
                    }`}
                  >
                    {/* The leading dot is what tells a state pill from a label
                      pill in a row that mixes them — the form rule 067 fixed. */}
                    <span
                      aria-hidden="true"
                      className={`size-2 rounded-full ${TASK_STATE_DOT[state]}`}
                    />
                    {stateCounts
                      ? words.counted(stateCounts[state])
                      : words.many}
                  </button>
                </li>
              );
            })}
          </ul>
        ) : null}

        {controls.hidden && onIncludeHiddenChange ? (
          <>
            {/*
            A divider, because this control is a different kind of thing from the ones
            before it: they narrow what is on screen, this refetches. The rule the
            board's own comment stated and its layout contradicted.
          */}
            <span
              aria-hidden="true"
              className="bg-border mx-0.5 hidden h-4 w-px sm:block"
            />
            <button
              type="button"
              aria-pressed={includeHidden}
              onClick={() => {
                onIncludeHiddenChange(!includeHidden);
              }}
              /*
              On the control rather than beside it. The sentence that used to sit in the
              header said something worth knowing once, and repeated it permanently in
              a row that has no space to spare — and appeared on toggle, pushing the
              board down as a result of the reader's own click.
            */
              title="Hidden tasks still count towards this workspace's spend."
              className={`${HEADER_PILL} ${
                includeHidden ? HEADER_PILL_ACTIVE : HEADER_PILL_IDLE
              } ${FOCUS_RING}`}
            >
              {includeHidden ? (
                <EyeIcon aria-hidden="true" className="size-3.5" />
              ) : (
                <EyeOffIcon aria-hidden="true" className="size-3.5" />
              )}
              {includeHidden ? 'Showing hidden' : 'Show hidden'}
            </button>
          </>
        ) : null}

        {controls.subtasks ? (
          <>
            <span
              aria-hidden="true"
              className="bg-border mx-0.5 hidden h-4 w-px sm:block"
            />
            <button
              type="button"
              aria-pressed={narrowing.excludeSubtasks === true}
              onClick={() => {
                onChange({
                  ...narrowing,
                  excludeSubtasks: !narrowing.excludeSubtasks,
                });
              }}
              title="Subtasks belong to a subworkflow another task launched."
              className={`${HEADER_PILL} ${
                narrowing.excludeSubtasks
                  ? HEADER_PILL_ACTIVE
                  : HEADER_PILL_IDLE
              } ${FOCUS_RING}`}
            >
              <GitBranchIcon aria-hidden="true" className="size-3.5" />
              {narrowing.excludeSubtasks ? 'Subtasks hidden' : 'Show subtasks'}
            </button>
          </>
        ) : null}

        {/*
        One press back to everything, wherever more than one thing is on.

        The label control clears its own labels; this clears the row. Rendered
        only when there is something to clear, and last, so it never displaces a
        control that is always there.
      */}
        {controls.clearAll !== false &&
        (narrowing.q !== undefined ||
          narrowing.state !== undefined ||
          narrowing.labels !== undefined ||
          narrowing.excludeSubtasks !== undefined) ? (
          <button
            type="button"
            onClick={() => {
              setQuery('');
              committed.current = '';
              onChange({});
            }}
            className={`${FOCUS_RING} text-muted-foreground hover:text-foreground touch-target inline-flex items-center justify-center rounded-full px-1.5 text-xs font-medium underline-offset-2 transition-colors hover:underline`}
          >
            Clear all
          </button>
        ) : null}
      </div>
    </div>
  );
}
